import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MessageIcon from "@mui/icons-material/ChatBubbleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import KeyIcon from "@mui/icons-material/VpnKey";
import RefreshIcon from "@mui/icons-material/Refresh";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DescriptionIcon from "@mui/icons-material/Description";
import { useEffect, useState } from "react";
import { foyerApi, type ScreenRecord } from "../http/foyer";
import type {
  FoyerCommand,
  ScreenHistoryEntry,
  ScreenRole,
  ScreenStatusEvent,
  DoorSignOrientation,
} from "@shared/types/foyer";
import { isScreenOnline } from "@shared/types/foyer";
import UptimeChart from "./UptimeChart";
import DeviceLogPanel from "./DeviceLogPanel";
import DeviceExitsPanel from "./DeviceExitsPanel";
import {
  platformLabel,
  capabilityLabel,
  commandLabel,
  captureModeLabel,
  managementLabel,
  roleLabel,
  relativeTime,
  screenshotFidelityNote,
  signagePlaybackLabel,
} from "../utils/devicePresentation";

interface Props {
  open: boolean;
  premiseId: string;
  screen: ScreenRecord | null;
  /** Whether the door-sign role is offered - true only on care premises. On a
   *  hotel the role control is hidden entirely (every screen is an in-room TV). */
  allowDoorSign?: boolean;
  /** Whether the signage role is offered - i.e. the place bought Signage. */
  allowSignage?: boolean;
  /** Whether screen sharing exists on this premise at all. False on a care
   *  premise: casting is a hospitality function, the boxes ship with Chromecast
   *  removed, and a care operator must not be shown a hotspot, a "Casting"
   *  capability or a rotate button for something their rooms cannot do.
   *  Defaults true so the chain-wide fleet view (Nenda staff, mixed premises)
   *  keeps the tool it has today. */
  showGuestCasting?: boolean;
  onClose: () => void;
  /** Called after destructive actions (unpair) so the parent refreshes. */
  onChanged?: () => void;
  /**
   * Called when the user clicks "Open preview" for this screen.
   *
   * The role travels with it because the preview it should open depends on it,
   * and because the dialog's own role mirror is fresher than `screen.role` -
   * flipping a screen to Signage and previewing it without closing the dialog
   * must not open the launcher and imply the flip did not take.
   */
  onOpenPreview: (screen: ScreenRecord, role: ScreenRole) => void;
}

/**
 * Display power state → human label. "On", "Standby (since <time>)", or "-"
 * when no power command has ever targeted the device. Reflects what the power
 * schedule has commanded (energy / ESG view).
 */
function formatPower(state?: "on" | "standby", atIso?: string): string {
  if (state === "on") return "On";
  if (state === "standby") {
    const since = atIso ? ` (since ${new Date(atIso).toLocaleString()})` : "";
    return `Standby${since}`;
  }
  return "-";
}

function formatUptime(ms?: number): string {
  if (!ms || ms < 0) return "-";
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours || days) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  return parts.join(" ");
}

/**
 * Wi-Fi RSSI (dBm) → "-55 dBm (Good)". Rough quality bands per the usual
 * Android thresholds. "-" when there's no reading (Ethernet, or signal absent).
 */
function formatSignal(dBm?: number): string {
  if (typeof dBm !== "number" || !Number.isFinite(dBm)) return "-";
  const quality =
    dBm >= -55
      ? "Excellent"
      : dBm >= -67
        ? "Good"
        : dBm >= -75
          ? "Fair"
          : "Weak";
  return `${dBm} dBm (${quality})`;
}

/** 30 × 2 s: long enough for a TV to render, encode and upload a capture. */
const DIAGNOSTIC_POLLS = 30;

/**
 * What a finished command means, for the toast. "acked" alone is only true of
 * delivery: a capture acked with an error, or with no file at all, did not
 * produce anything the operator asked for and must not read as a success.
 */
export function outcomeLabel(c: FoyerCommand): string {
  if (c.status !== "acked") return c.status;
  if (c.type !== "screenshot" && c.type !== "log_capture") return "acked";
  if (typeof c.result?.error === "string") return "failed on the device";
  if (typeof c.result?.assetUrl === "string") return "received";
  return "acked, but no file came back";
}

/** Chip colour for one command: a capture that came back empty is a failure
 *  whatever its delivery status says. */
function commandChipColor(
  c: FoyerCommand
): "success" | "warning" | "error" | "default" {
  const outcome = outcomeLabel(c);
  return outcome === "acked" || outcome === "received"
    ? "success"
    : c.status === "acked"
      ? "error"
      : commandStatusColor(c.status);
}

/** Delivery-status → MUI chip colour. */
function commandStatusColor(
  status: FoyerCommand["status"]
): "success" | "warning" | "error" | "default" {
  switch (status) {
    case "acked":
      return "success";
    case "sent":
    case "queued":
      return "warning";
    case "failed":
    case "expired":
      return "error";
    default:
      return "default";
  }
}

/**
 * Plain-language rendering of one audited device event. Staff read this to
 * answer "why did this TV change?", so a re-attach names the factor that
 * justified it - that is the whole point of auditing an action no human
 * approved. Unknown verbs fall through to the raw action rather than being
 * hidden: an event we forgot to describe must still be visible.
 */
function describeHistory(h: ScreenHistoryEntry): string {
  const ctx = h.context ?? {};
  const str = (k: string): string | null =>
    typeof ctx[k] === "string" ? (ctx[k] as string) : null;

  switch (h.action) {
    case "screen.reattach.serial": {
      const how =
        {
          token_possession: "it still held its previous token",
          provisioned_expectation: "it was pre-provisioned for this room",
          reattach_window: "staff had opened a re-attach window",
          known_network: "it came back on the room's own network while offline",
        }[str("factor") ?? ""] ?? str("factor");
      const room = str("roomId");
      return `Re-attached itself${room ? ` to room ${room}` : ""} - ${how}.`;
    }
    case "screen.reattach_window.open":
      return `${h.actor} opened a re-attach window${
        ctx.minutes ? ` (${String(ctx.minutes)} min)` : ""
      }.`;
    case "checkout.wipe.device":
      return h.outcome === "success"
        ? "Checkout wipe ran on the device."
        : "Checkout wipe ran, but the firmware did not confirm the clear.";
    case "checkin.signal.device":
      return "Check-in signalled to the device.";
    default:
      return `${h.action}${h.outcome !== "success" ? ` (${h.outcome})` : ""}`;
  }
}

export default function DeviceDetailDialog({
  open,
  premiseId,
  screen,
  allowDoorSign = false,
  allowSignage = false,
  showGuestCasting = true,
  onClose,
  onChanged,
  onOpenPreview,
}: Props) {
  // What the panel itself last said it was doing. Only a signage screen
  // reports; everything else has nothing to show and shows nothing.
  const playback = signagePlaybackLabel(screen?.live?.signage);
  const [events, setEvents] = useState<ScreenStatusEvent[]>([]);
  const [history, setHistory] = useState<ScreenHistoryEntry[]>([]);
  const [commands, setCommands] = useState<FoyerCommand[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Local copy so a regenerate updates the display without a full refetch.
  const [hotspot, setHotspot] = useState(screen?.guestHotspot);
  // Local role mirror so the toggle reflects the change immediately, before the
  // parent's next screens refetch swaps the `screen` prop.
  const [role, setRole] = useState<ScreenRole>(screen?.role ?? "tv");
  // Mount orientation for a door sign - landscape (default) vs portrait. Local
  // mirror, same pattern as `role`.
  const [orientation, setOrientation] = useState<DoorSignOrientation>(
    screen?.doorSignOrientation ?? "landscape"
  );

  function loadCommands(screenId: string) {
    foyerApi
      .screenCommands(screenId, 10)
      .then(setCommands)
      .catch(() => setCommands([]));
  }

  useEffect(() => {
    if (!open || !screen) return;
    setHotspot(screen.guestHotspot);
    setRole(screen.role ?? "tv");
    setOrientation(screen.doorSignOrientation ?? "landscape");
    foyerApi
      .screenStatusLog(screen.id, 14)
      .then(setEvents)
      .catch(() => setEvents([]));
    foyerApi
      .screenHistory(premiseId, screen.id, 20)
      .then(setHistory)
      .catch(() => setHistory([]));
    loadCommands(screen.id);
  }, [open, screen, premiseId]);

  if (!screen) return null;

  const live = screen.live;
  const online = isScreenOnline(screen, 60_000);
  // The box reports what its firmware can do; a care premise is not shown the
  // one capability it is not allowed to use (see `showGuestCasting`). The device
  // still reports it - this hides the chip, it does not rewrite telemetry.
  const capabilities = (live?.capabilities ?? []).filter(
    (c) => showGuestCasting || c !== "guestCasting"
  );

  async function withBusy<T>(label: string, fn: () => Promise<T>) {
    setBusy(label);
    setToast(null);
    try {
      await fn();
      setToast(`${label} · sent`);
      if (screen) loadCommands(screen.id);
      setTimeout(() => setToast(null), 2500);
    } catch (err) {
      setToast(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  const reboot = () =>
    withBusy("Reboot", () => foyerApi.rebootScreen(premiseId, screen.id));

  const sendMessage = () => {
    const body = prompt("Message body to show on this TV:");
    if (!body) return;
    return withBusy("Send message", () =>
      foyerApi.messageBulk({
        premiseId,
        screenIds: [screen.id],
        body,
        title: "Reception",
      })
    );
  };

  const refreshTelemetry = () =>
    withBusy("Refresh telemetry", () =>
      foyerApi.refreshTelemetry(premiseId, screen.id)
    );

  // Issue a diagnostic command, then poll the command log until the device has
  // uploaded + acked it (the result carries the asset URL). The device usually
  // responds in a second or two; we cap the wait so the UI never hangs.
  async function issueAndPoll(
    label: string,
    fn: () => Promise<{ commandId: string }>,
    // A diagnostic capture is a render + an upload from a TV-class CPU, and
    // routinely outlives the 24 s a plain command gets. Polling for less than
    // that is how a working capture came to read as "sent" forever.
    polls = 12
  ) {
    if (!screen) return;
    setBusy(label);
    setToast(null);
    try {
      const { commandId } = await fn();
      loadCommands(screen.id);
      for (let i = 0; i < polls; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const cmds = await foyerApi.screenCommands(screen.id, 10);
        setCommands(cmds);
        const done = cmds.find((c) => c.commandId === commandId);
        if (
          done &&
          (done.status === "acked" ||
            done.status === "failed" ||
            done.status === "expired")
        ) {
          setToast(`${label} · ${outcomeLabel(done)}`);
          break;
        }
        if (i === polls - 1) setToast(`${label} · no response`);
      }
      setTimeout(() => setToast(null), 2500);
    } catch (err) {
      setToast(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  const takeScreenshot = () =>
    issueAndPoll(
      "Screenshot",
      () => foyerApi.screenshotScreen(premiseId, screen.id),
      DIAGNOSTIC_POLLS
    );

  const captureLogs = () =>
    issueAndPoll(
      "Capture logs",
      () => foyerApi.captureLogs(premiseId, screen.id),
      DIAGNOSTIC_POLLS
    );

  // Display power, now. Offered ONLY where the device reports `powerControl`,
  // which on Samsung means the panel answered both setPanelMute and
  // getPanelMute - i.e. the switch can be confirmed against the hardware. A
  // button that silently does nothing is worse than no button, because an
  // operator then believes the room is dark.
  const canPower = live?.capabilities?.includes("powerControl") === true;
  const displayOn = live?.powerState !== "standby";
  const togglePower = () =>
    issueAndPoll(displayOn ? "Display off" : "Display on", () =>
      foyerApi.setScreenPower(premiseId, screen.id, !displayOn)
    );

  // Latest delivered diagnostic artifacts (commands are newest-first).
  const latestShot = commands.find(
    (c) =>
      c.type === "screenshot" &&
      c.status === "acked" &&
      typeof c.result?.assetUrl === "string"
  );
  const latestLogs = commands.find(
    (c) =>
      c.type === "log_capture" &&
      c.status === "acked" &&
      typeof c.result?.assetUrl === "string"
  );
  const shotUrl = latestShot?.result?.assetUrl as string | undefined;
  const logsUrl = latestLogs?.result?.assetUrl as string | undefined;
  // A capture the device TRIED and could not deliver.
  //
  // Without this the dialog renders identically whether a device never got the
  // command, never captured, or was refused by the upload endpoint - it simply
  // shows no artifact. That silence cost a bench session on 2026-08-12
  // ("I see SENT on capture logs ... but I don't see any log"), so a device
  // that reported why now gets to say so here. Only the newest attempt, and
  // only when it is newer than the artifact on screen - an old failure next to
  // a fresh capture would read as a live fault.
  const latestFailure = commands.find(
    (c) =>
      (c.type === "log_capture" || c.type === "screenshot") &&
      c.status === "acked" &&
      typeof c.result?.error === "string"
  );
  // Compared against the artifact of the SAME kind: a screenshot failure used
  // to be hidden by any newer log file, and vice versa.
  const latestOfFailedKind =
    latestFailure?.type === "screenshot" ? latestShot : latestLogs;
  const failureNote =
    latestFailure &&
    (!latestOfFailedKind ||
      new Date(latestFailure.createdAt) >
        new Date(latestOfFailedKind.createdAt))
      ? `${latestFailure.type === "screenshot" ? "Screenshot" : "Log"} capture failed on the device: ${String(latestFailure.result?.error)}`
      : null;
  // Whether a screenshot from THIS box can be trusted as a picture of the panel.
  // Shown next to the artifacts rather than buried in the capability chips: it is
  // the caveat that decides how to read the image right above it.
  const fidelityNote = screenshotFidelityNote(
    live?.platform,
    live?.capabilities
  );

  const changeRole = (next: ScreenRole) => {
    if (!screen || next === role) return;
    void withBusy("Set role", async () => {
      await foyerApi.setScreenRole(premiseId, screen.id, next);
      setRole(next);
      onChanged?.();
    });
  };

  const changeOrientation = (next: DoorSignOrientation) => {
    if (!screen || next === orientation) return;
    void withBusy("Set orientation", async () => {
      await foyerApi.setScreenOrientation(premiseId, screen.id, next);
      setOrientation(next);
      onChanged?.();
    });
  };

  const regenerateHotspot = () =>
    withBusy("Regenerate hotspot", async () => {
      const next = await foyerApi.regenerateGuestHotspot(premiseId, screen.id);
      setHotspot(next);
    });

  // Re-attach window: a serial alone can't re-bind a TV (it's printed on the
  // chassis, so it's an identifier, not a credential). This is the operator
  // override for a recovery the automatic factors can't cover - deliberately
  // short-lived, one device at a time, and audited.
  const allowReattach = () =>
    withBusy("Allow re-attach", async () => {
      const res = await foyerApi.openReattachWindow(premiseId, screen.id, 15);
      setToast(
        `Re-attach allowed until ${new Date(res.until).toLocaleTimeString()}`
      );
      setTimeout(() => setToast(null), 6000);
    });

  const unpair = async () => {
    if (!confirm("Unpair this TV?")) return;
    await foyerApi.unpairScreen(premiseId, screen.id);
    onChanged?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="h6" component="div">
            {screen.roomId ? `Room ${screen.roomId}` : "Unassigned TV"}
          </Typography>
          <Chip
            size="small"
            color={online ? "success" : "default"}
            label={online ? "online" : "offline"}
          />
          <Typography
            variant="caption"
            sx={{
              ml: 1,
              fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
              color: "text.secondary",
            }}
          >
            {screen.id}
          </Typography>
        </Stack>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="flex-start"
        >
          {/* Left: telemetry. Both columns need an explicit basis - the
              actions column carries a long help paragraph, and left to size
              itself it takes the whole row and wraps telemetry values one
              character per line. */}
          <Box sx={{ flex: "1 1 300px", minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">
              Telemetry
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "max-content 1fr",
                columnGap: 2,
                rowGap: 0.75,
                mt: 1,
              }}
            >
              <Row label="Status" value={online ? "Online" : "Offline"} />
              {/*
                ⚠️ EVERY `live?.*` ROW BELOW IS A POINT-IN-TIME READING, NOT A
                CURRENT ONE - and until 2026-09-09 nothing on this dialog said
                so. Firmware, platform, app/bundle versions, uptime, IPs and
                signal all come from the last `ScreenSnapshot` the device
                wrote; only Room, Serial and Model are current facts held by
                the CMS itself.

                What that cost: hasselbacken/302 was read as a healthy device
                "with model and firmware and all" while it had in fact been
                silent for 22 hours and was sitting on the pairing screen. The
                dialog was showing a fresh Model beside day-old firmware in
                identical type, with "Offline" one row up and a timestamp
                fifteen rows down, and nothing joining them.

                Same rule as the ESG figures in CLAUDE.md: a number nobody can
                date is a number that gets believed. So when the device is not
                online, the readings are labelled with their age, once, at the
                top of the block they apply to.
              */}
              {!online && live && (
                <Row
                  label="Readings"
                  value={`from ${relativeTime(live.lastSeenAt)}, not current`}
                  tone="warn"
                />
              )}
              <Row
                label="Power"
                value={formatPower(live?.powerState, live?.powerStateAt)}
              />
              <Row label="Room" value={screen.roomId ?? "-"} />
              {allowDoorSign && <Row label="Role" value={roleLabel(role)} />}
              {/*
                What the wall is ACTUALLY doing, straight from the panel.
                Shown only for a signage screen and only once one has reported:
                every other role has nothing to say here, and an empty "Playback
                -" row on a bedroom TV would just be noise.
              */}
              {playback && (
                <Row
                  label="Playback"
                  value={
                    playback.detail
                      ? `${playback.text} — ${playback.detail}`
                      : playback.text
                  }
                  tone={playback.severity}
                />
              )}
              <Row
                label="Model"
                value={live?.model ?? screen.model ?? "unknown"}
              />
              <Row label="Platform" value={platformLabel(live?.platform)} />
              <Row label="Serial" value={screen.serial ?? "-"} mono />
              <Row label="Firmware" value={live?.firmware ?? "-"} mono />
              <Row label="Resolution" value={live?.resolution ?? "-"} mono />
              <Row label="App version" value={live?.appVersion ?? "-"} mono />
              <Row
                label="Management"
                value={managementLabel(live?.platform, live?.capabilities)}
              />
              <Row label="Web bundle" value={live?.webVersion ?? "-"} mono />
              <Row
                label="Streaming app"
                value={live?.streamingAppVersion ?? "-"}
                mono
              />
              <Row label="User agent" value={live?.userAgent ?? "-"} mono />
              <Row label="Uptime" value={formatUptime(live?.uptimeMs)} mono />
              <Row label="Wi-Fi IP" value={live?.wifiIp ?? "-"} mono />
              <Row label="Ethernet IP" value={live?.ethIp ?? "-"} mono />
              <Row
                label="Wi-Fi signal"
                value={formatSignal(live?.wifiSignal)}
                mono
              />
              <Row
                label="Last seen"
                value={
                  screen.lastSeenAt
                    ? new Date(screen.lastSeenAt).toLocaleString()
                    : "never"
                }
              />
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" color="text.secondary">
              Capabilities
            </Typography>
            <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {capabilities.length ? (
                capabilities.map((c) => (
                  <Chip
                    key={c}
                    size="small"
                    variant="outlined"
                    label={capabilityLabel(c)}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              )}
            </Box>

            {showGuestCasting && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="overline" color="text.secondary">
                  Guest casting
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "max-content 1fr",
                    columnGap: 2,
                    rowGap: 0.75,
                    mt: 1,
                  }}
                  data-testid="device-guest-casting"
                >
                  <Row label="Hotspot SSID" value={hotspot?.ssid ?? "-"} mono />
                  <Row
                    label="Hotspot password"
                    value={hotspot?.password ?? "-"}
                    mono
                  />
                </Box>
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" color="text.secondary">
              Online history
            </Typography>
            <Box sx={{ mt: 1 }}>
              <UptimeChart events={events} days={14} />
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" color="text.secondary">
              Device history
            </Typography>
            <Box sx={{ mt: 1 }}>
              {history.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nothing recorded yet.
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {history.map((h, i) => (
                    <Stack
                      key={`${h.at}-${i}`}
                      direction="row"
                      spacing={1}
                      alignItems="baseline"
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ minWidth: 130 }}
                      >
                        {new Date(h.at).toLocaleString()}
                      </Typography>
                      <Typography variant="body2">
                        {describeHistory(h)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" color="text.secondary">
              Diagnostics
            </Typography>
            <Box sx={{ mt: 1 }}>
              {shotUrl ? (
                <a href={shotUrl} target="_blank" rel="noopener noreferrer">
                  <Box
                    component="img"
                    src={shotUrl}
                    alt="Latest screenshot"
                    sx={{
                      width: "100%",
                      maxWidth: 360,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      display: "block",
                    }}
                  />
                </a>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No screenshot yet - capture one from Actions.
                </Typography>
              )}
              {latestShot?.createdAt && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                  data-testid="screenshot-provenance"
                >
                  Captured {new Date(latestShot.createdAt).toLocaleString()}
                  {" · "}
                  {captureModeLabel(latestShot.result?.captureMode)}
                </Typography>
              )}
              {logsUrl && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <a href={logsUrl} target="_blank" rel="noopener noreferrer">
                    Download latest logs
                  </a>
                  {latestLogs?.result?.captureMode === "in-app" && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      Console buffer only - no native device log on this box.
                    </Typography>
                  )}
                </Typography>
              )}
              {failureNote && (
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{ display: "block", mt: 1 }}
                  data-testid="capture-failure-note"
                >
                  {failureNote}
                </Typography>
              )}
              {fidelityNote && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ display: "block", mt: 1 }}
                  data-testid="screenshot-fidelity-note"
                >
                  {fidelityNote}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" color="text.secondary">
              Crashes &amp; restarts
            </Typography>
            <Box sx={{ mt: 1 }}>
              <DeviceExitsPanel
                premiseId={premiseId}
                screenId={screen.id}
                platform={live?.platform}
              />
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" color="text.secondary">
              Device log
            </Typography>
            <Box sx={{ mt: 1 }}>
              <DeviceLogPanel premiseId={premiseId} screenId={screen.id} />
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" color="text.secondary">
              Recent commands
            </Typography>
            <Box sx={{ mt: 1 }}>
              {commands.length ? (
                <Stack spacing={0.75}>
                  {commands.map((c) => (
                    <Stack
                      key={c.commandId}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                    >
                      <Chip
                        size="small"
                        color={commandChipColor(c)}
                        variant={c.status === "acked" ? "filled" : "outlined"}
                        label={outcomeLabel(c)}
                      />
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                        {commandLabel(c)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {new Date(c.createdAt).toLocaleTimeString()}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No commands yet
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right: actions */}
          <Stack
            spacing={1}
            sx={{
              flex: "0 1 340px",
              minWidth: { xs: 0, md: 260 },
              width: "100%",
            }}
          >
            {(allowDoorSign || allowSignage) && (
              <>
                <Typography variant="overline" color="text.secondary">
                  Role
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  fullWidth
                  value={role}
                  onChange={(_e, v) => v && changeRole(v as ScreenRole)}
                  disabled={busy !== null}
                  aria-label="Screen role"
                  data-testid="device-role-toggle"
                >
                  <ToggleButton value="tv" data-testid="device-role-tv">
                    TV
                  </ToggleButton>
                  {allowDoorSign && (
                    <ToggleButton
                      value="door-sign"
                      data-testid="device-role-door-sign"
                    >
                      Door sign
                    </ToggleButton>
                  )}
                  {allowSignage && (
                    <ToggleButton
                      value="signage"
                      data-testid="device-role-signage"
                    >
                      Signage
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
                {/* Only the door-sign note. The signage half said what the
                    Signage plan already says, on a screen an operator reaches
                    by choosing Signage - see the role picker above it. */}
                {role === "door-sign" && (
                  <Typography variant="caption" color="text.secondary">
                    Door signs mount outside a room and show the resident&apos;s
                    name/status (PHI).
                  </Typography>
                )}
                {role === "door-sign" && (
                  <>
                    <Typography variant="overline" color="text.secondary">
                      Orientation
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      fullWidth
                      value={orientation}
                      onChange={(_e, v) =>
                        v && changeOrientation(v as DoorSignOrientation)
                      }
                      disabled={busy !== null}
                      aria-label="Door sign orientation"
                      data-testid="device-orientation-toggle"
                    >
                      <ToggleButton
                        value="landscape"
                        data-testid="device-orientation-landscape"
                      >
                        Landscape
                      </ToggleButton>
                      <ToggleButton
                        value="portrait"
                        data-testid="device-orientation-portrait"
                      >
                        Portrait
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary">
                      Match how this sign is physically mounted.
                    </Typography>
                  </>
                )}
              </>
            )}

            <Typography variant="overline" color="text.secondary">
              Actions
            </Typography>
            <Button
              startIcon={<RestartAltIcon />}
              variant="outlined"
              onClick={reboot}
              disabled={busy !== null}
              data-testid="device-reboot"
            >
              Reboot
            </Button>
            {/* Only the CAVEAT. "Restarts the device" is what the button says
                it does, so saying it again underneath is noise - but a box that
                can only reload the page cannot recover the frozen WebView an
                operator is usually reaching for it to fix, and nothing else on
                this screen says so.

                Reworded 2026-09-09. It used to name device-owner enrolment as
                the reason, which is the ANDROID reason and reads as a
                configuration detail rather than a limit. On a Tizen panel the
                cause is different (no native bridge at all) and the
                consequence is bigger: a reload re-runs the current document,
                so it cannot bring a panel back into its widget origin either.
                An operator pressing this four times on hasselbacken/302 got
                four green acks and no restart. Say the limit, not the cause. */}
            {!live?.capabilities?.includes("rebootDevice") && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="device-reboot-caption"
              >
                This will NOT restart the TV - it reloads the page on screen.
                This device cannot be restarted remotely, so a frozen screen, or
                a panel that has dropped out of its app shell, needs someone to
                power-cycle it in the room.
              </Typography>
            )}
            <Button
              startIcon={<MessageIcon />}
              variant="outlined"
              onClick={sendMessage}
              disabled={busy !== null}
              data-testid="device-message"
            >
              Send message
            </Button>
            {canPower && (
              <>
                <Button
                  startIcon={<PowerSettingsNewIcon />}
                  variant="outlined"
                  onClick={togglePower}
                  disabled={busy !== null}
                  data-testid="device-power"
                >
                  {displayOn ? "Display off" : "Display on"}
                </Button>
              </>
            )}
            <Button
              startIcon={<RefreshIcon />}
              variant="outlined"
              onClick={refreshTelemetry}
              disabled={busy !== null}
              data-testid="device-refresh-telemetry"
            >
              Refresh telemetry
            </Button>
            <Button
              startIcon={<PhotoCameraIcon />}
              variant="outlined"
              onClick={takeScreenshot}
              disabled={busy !== null}
              data-testid="device-screenshot"
            >
              Capture screenshot
            </Button>
            <Button
              startIcon={<DescriptionIcon />}
              variant="outlined"
              onClick={captureLogs}
              disabled={busy !== null}
              data-testid="device-capture-logs"
            >
              Capture logs
            </Button>
            <Button
              startIcon={<OpenInNewIcon />}
              variant="outlined"
              onClick={() => onOpenPreview(screen, role)}
              data-testid="device-open-preview"
            >
              {role === "signage" ? "Open signage preview" : "Open preview"}
            </Button>
            {showGuestCasting && (
              <Button
                startIcon={<WifiTetheringIcon />}
                variant="outlined"
                onClick={regenerateHotspot}
                disabled={busy !== null}
                data-testid="device-regenerate-hotspot"
              >
                Regenerate hotspot
              </Button>
            )}
            <Button
              startIcon={<KeyIcon />}
              variant="outlined"
              onClick={allowReattach}
              disabled={busy !== null || !screen.serial}
              title={
                screen.serial
                  ? "Let this TV re-bind from its serial for the next 15 minutes"
                  : "No hardware serial on record - nothing to re-attach by"
              }
              data-testid="device-allow-reattach"
            >
              Allow re-attach (15 min)
            </Button>
            <Divider sx={{ my: 1 }} />
            <Button
              startIcon={<LinkOffIcon />}
              color="error"
              variant="outlined"
              onClick={unpair}
              disabled={busy !== null}
              data-testid="device-unpair"
            >
              Unpair
            </Button>
            {(busy || toast) && (
              <Typography variant="caption" color="text.secondary">
                {busy ? `${busy}…` : toast}
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function Row({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  /** Colours the VALUE only. A fault has to be legible in a wall of grey
   *  telemetry rows, and colouring the label would just shout the word. */
  tone?: "ok" | "warn" | "error";
}) {
  return (
    <>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ alignSelf: "baseline" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: mono
            ? 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
            : undefined,
          wordBreak: "break-all",
          color:
            tone === "error"
              ? "error.main"
              : tone === "warn"
                ? "warning.main"
                : undefined,
          fontWeight: tone && tone !== "ok" ? 600 : undefined,
        }}
      >
        {value}
      </Typography>
    </>
  );
}
